import { useEffect, useState } from "react";
import TimeSlot from "./TimeSlot";
import { useLocation, useNavigate } from "react-router-dom";
import "../../style/css/style.css";
import { PlanData } from "../../interfaces/Pod.interface";
import {
  bookPodWithPlan,
  fetchPlanDataByPod,
} from "../../services/PodService";
import { useOrganization } from "../../context/OrganizationContext";
import Loader from "../common/Loader";
import useRazorpay from "../../utils/displayRazorpay";
import { toast } from "react-toastify";
import { motion } from "framer-motion";
import { isAuthenticated } from "../../services/AuthService";

interface TimeCardProps {
  selectedPodId?: string | null;
  timeExtended?: boolean;
  handleModalClose: () => void;
}

// Use email from localStorage instead of hardcoded value

const TimeCard: React.FC<TimeCardProps> = ({
  selectedPodId,
  timeExtended,
  handleModalClose,
}) => {
  const navigate = useNavigate();
  const [selectedSlot, setSelectedSlot] = useState<number>(0);
  const [data, setData] = useState<PlanData[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const { displayRazorpay } = useRazorpay();
  const { organizationData } = useOrganization();

  const handleSlotChange = (slotIndex: number) => {
    setSelectedSlot(slotIndex);
  };

  const handleConfirmClick = async () => {
    // Check if user is authenticated
    const isUserAuthenticated = isAuthenticated();
    const userEmail = localStorage.getItem('auth_email');
    const isPaymentEnabled = organizationData?.is_payment_enabled;
    const isLoginEnabled = organizationData?.login_enabled;
    
    // If not authenticated or email not available, redirect to auth page
    const isPaymentEnabledAndUserNotAuthenticated = isPaymentEnabled && (!isUserAuthenticated || !userEmail);
    const isLoginEnabledAndUserNotAuthenticated = isLoginEnabled && (!isUserAuthenticated || !userEmail);
    if (isPaymentEnabledAndUserNotAuthenticated || isLoginEnabledAndUserNotAuthenticated) {
      // Store current pod and slot selection in localStorage to potentially return to this state
      if (selectedPodId) {
        localStorage.setItem('selected_pod_id', selectedPodId);
        localStorage.setItem('selected_slot_index', selectedSlot.toString());
      }
      
      // Close the modal before redirecting
      handleModalClose();
      
      // Redirect to authentication page
      toast.info('Please log in to continue with your booking');
      navigate('/auth');
      return;
    }
    
    const product = data[selectedSlot];
    if (product) {
      if (isPaymentEnabled && selectedPodId && userEmail) {
        // Use email from localStorage instead of hardcoded value
        await displayRazorpay(selectedSlot, selectedPodId, data, userEmail);
      } else if (selectedSlot !== null) {
        // logic for non payment enabled with booking
        const postDataToSend = {
          plan_id: data[selectedSlot]?.id,
          pod_id: selectedPodId,
        };
        
        bookPodWithPlan(postDataToSend)
          .then((response) => {
            if (response.status.success) {
              const bookingDetails = {
                ...response.data,
                plan_name: data[selectedSlot]?.plan_name,
                pod_id: selectedPodId,
              };
              localStorage.setItem(
                "bookingDetails",
                JSON.stringify(bookingDetails)
              );
              navigate("/thankyou");
            } else {
              toast.error(response.status.errorMessage);
              handleModalClose();
            }
          })
          .catch((error) => {
            console.error("Error posting data:", error);
            toast.error("Failed to complete your booking. Please try again.");
          });
      }
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const responseData = await fetchPlanDataByPod(selectedPodId);
        setData(responseData);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  return (
    <div className="flex flex-col h-full">
      <div className="time-card-container bg-white flex-grow">
        <div className="flex flex-col items-center">
          <div className="mt-4 mb-6 border-b-4 border-customBorderGrey rounded-md w-10"></div>
          <div className="text-lg text-customBlack font-semibold">
            Choose from the best
          </div>
          <div className="text-sm text-customTextGrey font-normal pb-4">
            Select your nap time
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center items-center my-6">
            <Loader isCompact />
          </div>
        ) : data.length === 0 ? (
          <div className="flex justify-center items-center my-6 font-bold text-base">
            <div>No Plan found</div>
            <span role="img" aria-label="Sad face" className="ml-2">
              😔
            </span>
          </div>
        ) : (
          data
            .sort((a, b) => a.duration - b.duration)
            .map((item, index) => (
              <div className="p-2" key={item.id}>
                <TimeSlot
                  slotData={item}
                  slotIndex={index}
                  isChecked={selectedSlot === index}
                  handleChange={handleSlotChange}
                />
              </div>
            ))
        )}
        <div className="flex justify-center mt-4">
          <motion.button
            className={`confirm-button hover:bg-blue-700 text-white font-bold py-2 sm:py-2 sm:px-4 px-6 rounded-lg text-sm sm:text-base ${
              selectedSlot === null
                ? "bg-[#E0DDDD] cursor-not-allowed"
                : "bg-customBlue"
            } w-[95%] sm:w-[330px] h-[46px] mb-8`}
            onClick={handleConfirmClick}
            disabled={selectedSlot === null}
            whileTap={{ scale: 0.95 }}
          >
            CONFIRM
          </motion.button>
        </div>
      </div>
    </div>
  );
};

export default TimeCard;
